import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Menu, MenuItem } from '@mui/material';
import React, { useState } from 'react';
import { utils, write } from 'xlsx';

import { appendLegendToSvg, type LegendExportConfig } from './utils/svgLegend';

type Props = {
  getSvg?: () => SVGSVGElement | null;
  computeFileBaseName: () => string;
  onDownloadPng?: () => Promise<void> | void;
  getCsv?: () => string | undefined;
  getExcelRows?: () => Array<Array<string | number>> | undefined;
  getLegendExportConfig?: () => LegendExportConfig | undefined;
};

export default function ExportMenu({
  getSvg,
  computeFileBaseName,
  onDownloadPng,
  getCsv,
  getExcelRows,
  getLegendExportConfig,
}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const canExportSvg = typeof getSvg === 'function';
  const canExportPng = canExportSvg || typeof onDownloadPng === 'function';

  const pngScale = 4;
  const pngMinWidth = 1200;
  const pngMinHeight = 600;

  function onOpen(e: React.MouseEvent<HTMLElement>) {
    setAnchorEl(e.currentTarget);
  }

  function onClose() {
    setAnchorEl(null);
  }

  function serializeSvg(svgEl: SVGSVGElement) {
    const clone = svgEl.cloneNode(true) as SVGSVGElement;
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
    // Inject export-only legend if provided
    try {
      const cfg = getLegendExportConfig ? getLegendExportConfig() : undefined;
      if (cfg && cfg.items && cfg.items.length > 0) {
        appendLegendToSvg(clone, cfg);
      }
    } catch {
      // Ignore errors
    }
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(clone);
    return `<?xml version="1.0" standalone="no"?>\r\n${source}`;
  }

  function downloadBlob(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function handleDownloadSVG() {
    if (!getSvg) return;
    const svg = getSvg();
    if (!svg) return;
    const data = serializeSvg(svg);
    const blob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
    downloadBlob(blob, `${computeFileBaseName()}.svg`);
    onClose();
  }

  async function handleDownloadPNG() {
    if (onDownloadPng) {
      await onDownloadPng();
      onClose();
      return;
    }

    if (!getSvg) return;
    const svg = getSvg();
    if (!svg) return;
    const data = serializeSvg(svg);
    const svgUrl = `data:image/svg+xml;base64,${window.btoa(unescape(encodeURIComponent(data)))}`;
    const img = new Image();
    const width =
      (svg.viewBox && svg.viewBox.baseVal && svg.viewBox.baseVal.width) ||
      svg.clientWidth ||
      800;
    const height =
      (svg.viewBox && svg.viewBox.baseVal && svg.viewBox.baseVal.height) ||
      svg.clientHeight ||
      400;
    const canvas = document.createElement('canvas');
    const outW = Math.max(Math.ceil(width * pngScale), pngMinWidth);
    const outH = Math.max(Math.ceil(height * pngScale), pngMinHeight);
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    await new Promise<void>((resolve) => {
      img.onload = () => {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve();
      };
      img.src = svgUrl;
    });
    canvas.toBlob((blob) => {
      if (blob) downloadBlob(blob, `${computeFileBaseName()}.png`);
    });
    onClose();
  }

  function handleDownloadCSV() {
    if (!getCsv) return;
    const csv = getCsv();
    if (!csv) return;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, `${computeFileBaseName()}.csv`);
    onClose();
  }

  function handleDownloadExcel() {
    if (!getExcelRows) return;
    const rows = getExcelRows();
    if (!rows) return;
    const ws = utils.aoa_to_sheet(rows);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Data');
    const wbout = write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    downloadBlob(blob, `${computeFileBaseName()}.xlsx`);
    onClose();
  }

  return (
    <>
      <IconButton
        id='export-menu-button'
        size='small'
        aria-label='export menu'
        aria-controls={open ? 'export-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={onOpen}
      >
        <MoreVertIcon fontSize='small' />
      </IconButton>
      <Menu
        id='export-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{
          'aria-labelledby': 'export-menu-button',
        }}
      >
        {canExportSvg && (
          <MenuItem onClick={handleDownloadSVG}>Download SVG</MenuItem>
        )}
        {canExportPng && (
          <MenuItem onClick={() => void handleDownloadPNG()}>
            Download PNG
          </MenuItem>
        )}
        {getCsv && (
          <MenuItem onClick={handleDownloadCSV}>Download CSV</MenuItem>
        )}
        {getExcelRows && (
          <MenuItem onClick={handleDownloadExcel}>Download Excel</MenuItem>
        )}
      </Menu>
    </>
  );
}
